import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

export interface TimeSlot {
  label: string;
  hour12: number;
  minute: number;
  zone: string;
  disabled: boolean;
}

@Component({
  selector: 'app-ff-time-picker',
  templateUrl: './ff-time-picker.component.html',
  styleUrls: ['./ff-time-picker.component.scss']
})
export class FfTimePickerComponent implements OnInit, OnChanges {

  selectedSlot: TimeSlot | null = null;
  allSlots: TimeSlot[] = [];
  filteredSlots: TimeSlot[] = [];
  searchQuery = '';

  @Output() onTimePicked: EventEmitter<any> = new EventEmitter<any>();
  @Input() dateSelected: string;
  @Input() step: number;
  @Input() restrict90Minutes: boolean;

  private refHour24 = 0;
  private refMinute = 0;
  todaySelected = false;

  get referenceTimeLabel(): string {
    const h = this.refHour24 % 12 || 12;
    const m = this.padZero(this.refMinute);
    const zone = this.refHour24 >= 12 ? 'PM' : 'AM';
    return `${h}:${m} ${zone}`;
  }

  get instructionsText(): string {
    if (this.todaySelected) {
      return `Last unavailable: ${this.referenceTimeLabel}. Available slots below.`;
    }
    return 'Select an available time slot.';
  }

  /** Only enabled (available) slots from the current search filter. */
  get availableSlots(): TimeSlot[] {
    return this.filteredSlots.filter(s => !s.disabled);
  }

  constructor() {}

  ngOnInit() {
    if (!this.step) this.step = 15;
    this.computeReference();
    this.buildSlots();
    this.applyFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateSelected'] || changes['restrict90Minutes']) {
      this.computeReference();
      this.buildSlots();
      this.applyFilter();
    }
  }

  private computeReference(): void {
    if (!this.dateSelected) return;
    const now = new Date();
    const target = new Date(this.dateSelected);
    this.todaySelected =
      target.getDate() === now.getDate() &&
      target.getMonth() === now.getMonth() &&
      target.getFullYear() === now.getFullYear();

    if (!this.todaySelected) {
      this.refHour24 = 0;
      this.refMinute = 0;
      return;
    }

    let hrs = now.getHours();
    let mins = now.getMinutes();

    if (this.restrict90Minutes) {
      mins += 90;
      if (mins >= 60) {
        hrs += Math.floor(mins / 60);
        mins = mins % 60;
      }
    }

    this.refHour24 = hrs;
    this.refMinute = mins;
  }

  /** Chronological hour order for 12h clock: 12, 1, 2, ..., 11 */
  private readonly hourOrder12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  private buildSlots(): void {
    const slots: TimeSlot[] = [];
    const minutes = this.getStepMinutes();

    for (const zone of ['AM', 'PM']) {
      for (const hour12 of this.hourOrder12) {
        for (const minute of minutes) {
          const h24 = this.to24(hour12, zone);
          const disabled = this.isSlotDisabled(h24, minute);
          const label = this.formatSlotLabel(hour12, minute, zone);
          slots.push({
            label,
            hour12,
            minute,
            zone,
            disabled
          });
        }
      }
    }
    this.allSlots = slots;
  }

  private getStepMinutes(): number[] {
    const list: number[] = [];
    for (let m = 0; m < 60; m += this.step) list.push(m);
    return list;
  }

  private formatSlotLabel(hour12: number, minute: number, zone: string): string {
    const h = String(hour12);
    const m = this.padZero(minute);
    return `${h}:${m} ${zone}`;
  }

  private to24(hour12: number, zone: string): number {
    if (zone === 'AM') return hour12 === 12 ? 0 : hour12;
    return hour12 === 12 ? 12 : hour12 + 12;
  }

  private isSlotDisabled(hour24: number, minute: number): boolean {
    if (!this.todaySelected) return false;
    if (hour24 < this.refHour24) return true;
    if (hour24 > this.refHour24) return false;
    return minute <= this.refMinute;
  }

  padZero(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  onSearchInput(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      this.filteredSlots = [...this.allSlots];
      return;
    }
    this.filteredSlots = this.allSlots.filter(s => {
      const match = s.label.toLowerCase().includes(q) ||
        s.label.replace(/\s/g, '').includes(q.replace(/\s/g, ''));
      return match;
    });
  }

  onSlotClick(slot: TimeSlot): void {
    if (slot.disabled) return;
    this.selectedSlot = slot;
    this.emitPicked();
  }

  private emitPicked(): void {
    if (!this.selectedSlot) return;
    setTimeout(() => {
      this.onTimePicked.emit({
        hour: this.selectedSlot!.hour12,
        minute: this.selectedSlot!.minute,
        zone: this.selectedSlot!.zone
      });
    }, 150);
  }
}
